const EXCLUDED = 'script,style,iframe,object,embed,link,base,meta,input[type="password"],input[type="hidden"],[data-support-private],[data-support-ui]';

// A detached document never executes the copied page. Rendering is a separate,
// opt-in adapter and must not fall back to capturing a later live page state.
export function prepareSource(element, viewport) {
  const snapshot = document.implementation.createHTMLDocument('Support source');
  if (element.querySelectorAll('*').length > 10000) return { document: null, viewport, dispose() {} };
  const clone = element.cloneNode(true);
  const sourceNodes = [element, ...element.querySelectorAll('*')];
  [clone, ...clone.querySelectorAll('*')].forEach((node, index) => {
    const computed = window.getComputedStyle(sourceNodes[index]);
    const properties = [];
    for (let i = 0; i < computed.length; i += 1) {
      const name = computed[i]; const value = computed.getPropertyValue(name);
      if (!/url\s*\(/i.test(value)) properties.push(`${name}:${value}`);
    }
    node.setAttribute('style', properties.join(';'));
  });
  const originals = element.querySelectorAll('input,textarea,select');
  clone.querySelectorAll('input,textarea,select').forEach((field, index) => {
    Object.assign(field, { value: originals[index].value });
    if ('checked' in field) Object.assign(field, { checked: originals[index].checked });
  });
  clone.querySelectorAll(EXCLUDED).forEach(node => node.remove());
  [clone, ...clone.querySelectorAll('*')].forEach(node => {
    [...node.attributes].forEach(attribute => {
      if (/^on/i.test(attribute.name) || ['src', 'srcset', 'href', 'action', 'formaction', 'poster'].includes(attribute.localName)) {
        node.removeAttributeNS(attribute.namespaceURI, attribute.localName);
      }
    });
  });
  snapshot.body.appendChild(clone);
  const handle = { document: snapshot, viewport: { ...viewport }, dispose() { this.document = null; } };
  return handle;
}

export async function renderPreview(handle) {
  const unavailable = { kind: 'unavailable', reason: 'Nie udało się przygotować zrzutu. Możesz dołączyć własny obraz PNG lub JPEG.' };
  if (!handle || !handle.document || !handle.viewport || handle.viewport.width * handle.viewport.height > 12000000) return unavailable;
  const frozen = handle.document;
  const viewport = { ...handle.viewport };
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true'); frame.setAttribute('data-support-ui', 'true'); frame.setAttribute('sandbox', 'allow-same-origin');
  frame.style.cssText = `position:fixed;left:-20000px;top:0;width:${viewport.width}px;height:${viewport.height}px;border:0;pointer-events:none`;
  try {
    const { default: html2canvas } = await import('html2canvas');
    if (!handle.document) return unavailable;
    document.body.appendChild(frame);
    const target = frame.contentDocument;
    const content = frozen.body.firstElementChild;
    if (!target || !content) return unavailable;
    const policy = target.createElement('meta');
    policy.httpEquiv = 'Content-Security-Policy';
    policy.content = "default-src 'none'; style-src 'unsafe-inline'; img-src data: blob:; frame-src 'self'";
    target.head.appendChild(policy);
    const body = target.importNode(content, true);
    target.documentElement.replaceChild(body, target.body);
    frame.contentWindow.scrollTo(viewport.scrollX || 0, viewport.scrollY || 0);
    const canvas = await html2canvas(body, { backgroundColor: '#ffffff',
      logging: false,
      allowTaint: false,
      useCORS: false,
      width: viewport.width,
      height: viewport.height,
      windowWidth: viewport.width,
      windowHeight: viewport.height,
      x: viewport.scrollX || 0,
      y: viewport.scrollY || 0,
      scale: 1,
      imageTimeout: 1500 });
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    return blob && blob.size <= 5 * 1024 * 1024 && handle.document ? { kind: 'ready', blob } : unavailable;
  } catch (error) { return unavailable; } finally { frame.remove(); }
}
